import { Component, ReactNode } from "react";
import request from "superagent";
import { Loader } from "@screencloud/screencloud-ui-components";

import { ScreenCloud } from "../../ScreenCloudReactApp";
import { PowerBiContainer } from "../PowerBiContainer/PowerBiContainer";
import ErrorBoundary from "../ErrorBoundary/ErrorBoundary";
import { BiBasicFilter } from "../../models/BiBasicFilter";
import getScreenDataFilters from "../../lib/GetScreenDataKeyValue";
import isCustomKeyField from "../../lib/IsCustomKeyField";
import getCustomKey from "../../lib/GetCustomKey";
import { BiReportPageSettings } from "../../models/BiReportPageSettings";
import { tryParseFilterValueString } from "../../util/filterHelpers";

/**
 * Establish fallback values.
 *
 * @remarks
 * These fallbacks are rarely needed because the configuration in Connections usually includes them, even if the user
 * opts for the default settings. However, to prevent any potential errors, we establish some sensible defaults.
 */
const FALLBACK_REFRESHMS = 300000;
const FALLBACK_PAGETIME = 60;

interface State {
  loading: boolean;
  appConfigId: string;
  credentialId: string;
  reportId: string;
  reportType: string;
  workspaceId: string;
  filters: BiBasicFilter[];
  pageSettings: BiReportPageSettings;
  urlFilter: string;
  refreshMilliseconds?: number;
}

interface Props {
  sc: ScreenCloud;
  appViewerToken?: string;
}

export class AppContainer extends Component<Props, State> {
  /**
   * Constructs the component.
   */
  constructor(props: Props) {
    super(props);
    const appConfigId = props.sc && props.sc.config.appConfigId;
    this.state = {
      loading: true,
      appConfigId,
      reportId: "",
      reportType: "",
      credentialId: "",
      workspaceId: "",
      filters: [],
      pageSettings: {
        pageChange: false,
        pageName: "",
        pageTime: FALLBACK_PAGETIME,
      },
      urlFilter: "",
      refreshMilliseconds: FALLBACK_REFRESHMS,
    };
  }

  /**
   * Invoked immediately after the component is mounted.
   *
   * @remarks
   * This method verifies the presence of `appConfigId` in the props. If it exists, the method fetches the definitive
   * configuration payload from DynamoDB using Connections. If `appConfigId` is missing (which is highly unlikely and
   * would probably indicate a migration issue), an error is thrown because the process cannot continue without it.
   *
   * @todo
   * Refactor app by creating a powerBiService class to do all interactions with the service as a big chunk of below is
   * just copy and pasted from the PowerBiContainer. For the sake of brevity, we will do this later.
   */
  async componentDidMount(): Promise<void> {
    try {
      if (!this.state.appConfigId || !this.props.appViewerToken) {
        throw new Error("Invalid request. Unable to continue.");
      }

      console.log(`Fetching data for appConfigId: ${this.state.appConfigId}`);

      // Get the API base URL from the env and check its valid
      let apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
      if (!apiBaseUrl || apiBaseUrl.trim() === "") {
        console.log("Unable to get the base api url");
        throw new Error(`Unable to get the base api url`);
      }

      // We need to strip off the `/power-bi` part of the URL for this call
      apiBaseUrl = apiBaseUrl.replace("/power-bi", "");

      // Ask for the config for this app
      const response = await request
        .get(`${apiBaseUrl}/config/${this.state.appConfigId}/`)
        .set("Authorization", `Bearer ${this.props.appViewerToken}`)
        .set("App-Instance-Id", this.props.sc.appInstanceId || "");
      const body = response.body;

      // Ensure its an object and at least contains a valid reportId
      if (
        typeof body !== "object" ||
        body === null ||
        !("reportId" in body) ||
        body.reportId === undefined
      ) {
        throw new Error("Unable to get app instance configuration.");
      }

      // Set the state and fallback to sensible defaults in case
      const reportId = body.reportId || "";
      const reportType = body.reportType || "";
      const filters = this.parseFilters(body.filters);
      const urlFilter = body.urlFilter || "";
      const workspaceId = body.workspaceId || "";
      const refreshMilliseconds =
        body.refreshMilliseconds || FALLBACK_REFRESHMS;
      const reportPageSettings: BiReportPageSettings = {
        pageChange: body.pageChange || false,
        pageName: body.pageName || "",
        pageTime: body.pageTime || FALLBACK_PAGETIME,
      };
      const state: Partial<State> = {
        reportId,
        reportType,
        workspaceId,
        pageSettings: reportPageSettings,
        filters,
        urlFilter,
        refreshMilliseconds,
      };
      console.log("Setting config from Connections:", state);
      this.setState(state as State);
    } catch (error) {
      console.log("Error during mounting:", error);
      this.props.sc.logger?.error(`AppContainer error during mount ${error}`);
    } finally {
      // Tell app we are not loading regardless what happens above and continue with the Studio payload.
      this.setState({ loading: false });
    }
  }

  /**
   * Parse the filters ready for display
   *
   * @param filters - Filter array in PowerBi format
   */
  parseFilters(str: string): BiBasicFilter[] {
    if (!str) {
      return [];
    }
    const filters: BiBasicFilter[] = JSON.parse(str);
    const screenData = this.props.sc.context.screenData;
    // no point in trying to populate fields if screen data isn't passed
    if (screenData != null) {
      // Iterate through each filter
      for (let i = 0; i < filters.length; i++) {
        // Check if each field of the filter is a Custom User Key Field
        if (isCustomKeyField(filters[i].table)) {
          const tableKey = getCustomKey(filters[i].table);
          const tableValue = getScreenDataFilters(screenData, tableKey);
          filters[i].table = tableValue;
        }
        if (isCustomKeyField(filters[i].column)) {
          const columnKey = getCustomKey(filters[i].column);
          const columnValue = getScreenDataFilters(screenData, columnKey);
          filters[i].column = columnValue;
        }
        const fieldKey = filters[i].values[0];
        if (typeof fieldKey === "string" && isCustomKeyField(fieldKey)) {
          let prefix = "";
          if (fieldKey.startsWith("N::") || fieldKey.startsWith("B::")) {
            prefix = fieldKey.substring(0, 3);
          }
          const valuesKey = getCustomKey(fieldKey);
          const dataValues = getScreenDataFilters(screenData, valuesKey);
          filters[i].values = dataValues.split(",").map((value) => {
            return prefix + value;
          });
        }
      }
    }
    // post processing of filters - parse to number or boolean where possible
    filters.forEach((filter) => {
      const processedFilterValues: (string | number | boolean)[] = [];
      filter.values.forEach((filterValue) => {
        if (typeof filterValue === "string") {
          let prefix = "";
          if (filterValue.startsWith("N::") || filterValue.startsWith("B::")) {
            prefix = filterValue.substring(0, 3);
            filterValue = filterValue.substring(3);
          }
          const splitValues = filterValue.split(",");
          splitValues.forEach((value) => {
            processedFilterValues.push(
              tryParseFilterValueString(prefix + value)
            );
          });
        } else {
          processedFilterValues.push(filterValue);
        }
      });
      filter.values = processedFilterValues;
    });
    return filters;
  }

  /**
   * Renders the component.
   *
   * @remarks
   * The component delays rendering the PowerBiContainer until it receives the configuration from Connections, if an
   * `appConfigId` is provided. This configuration fetch is initiated in the `componentDidMount` method.
   */
  render(): ReactNode {
    if (this.state.loading) {
      return (
        <div className="loader-container">
          <div className="loader-wrapper">
            <Loader inline size="medium" active />
            <p style={{ marginTop: 16 }}>Loading</p>
          </div>
        </div>
      );
    }
    return (
      <ErrorBoundary>
        <div className="app-container">
          {this.props.sc && this.props.appViewerToken && (
            <PowerBiContainer
              appConfigId={this.state.appConfigId}
              credentialId={this.state.credentialId}
              reportId={this.state.reportId}
              reportType={this.state.reportType}
              workspaceId={this.state.workspaceId}
              filters={this.state.filters}
              pageSettings={this.state.pageSettings}
              appStarted={this.props.sc.appStarted}
              urlFilter={this.state.urlFilter}
              refreshMilliseconds={this.state.refreshMilliseconds}
              orgId={this.props.sc.orgId}
              logger={this.props.sc.logger}
              appViewerToken={this.props.appViewerToken}
              appInstanceId={this.props.sc.appInstanceId || ""}
            />
          )}
        </div>
      </ErrorBoundary>
    );
  }
}
